import { useState, useMemo } from 'react';
import type {
  InstagramCreator,
  InstagramCreatorAgeBucket,
  InstagramCreatorAudienceAgeBucketEntry,
  InstagramCreatorAudienceGenderEntry,
  InstagramCreatorGender,
  InstagramCreatorAudienceCountryEntry,
  InstagramCreatorAudienceCityEntry,
} from '@frontend/gql/social/graphql';
import { getCountryLabel } from '../../../components/filters-form/utils';
import { ageRangeDictionary } from '../table';

export type AgeDistributionChartItem = {
  name: string; // Display name for the age bucket (e.g., "18-24")
  value: number; // Actual percentage (e.g., 25.5 for 25.5%)
  fill: string; // Color for the pie slice
  key: InstagramCreatorAgeBucket; // Use the specific enum type for the key
};

// New type for Gender Distribution Chart
export interface GenderDistributionChartItem {
  key: InstagramCreatorGender;
  name: string;
  value: number;
  fill: string;
}

export interface MajorAudienceDemographicsSectionProps {
  activeDemographicTab: string;
  setActiveDemographicTab: (tab: string) => void;
  isLoading: boolean;
  ageBucket?: string;
  gender?: string;
  country?: string;
  city?: string;
  ageDistributionData?: AgeDistributionChartItem[];
  genderDistributionData?: GenderDistributionChartItem[];
  topCountriesData?: InstagramCreatorAudienceCountryEntry[];
  topCitiesData?: InstagramCreatorAudienceCityEntry[];
}

// Consistent color mapping for Age Buckets with the new palette
const AGE_BUCKET_COLOR_MAP: Record<InstagramCreatorAgeBucket | 'UNKNOWN_DEFAULT', string> = {
  AGE_18_TO_24: '#2F7772',
  AGE_25_TO_34: '#5A9692',
  AGE_35_TO_44: '#5DB8B3',
  AGE_45_TO_54: '#B2E5E2',
  AGE_55_TO_64: '#D0F6F3',
  AGE_65_AND_ABOVE: '#16282D',
  UNKNOWN: '#CCCCCC', // Confirmed new color for UNKNOWN
  UNKNOWN_DEFAULT: '#E0E0E0', // Neutral fallback color
};

// Color and display name mapping for Genders
const GENDER_COLOR_MAP: Record<InstagramCreatorGender | 'UNKNOWN_DEFAULT', string> = {
  MALE: '#2F7772',
  FEMALE: '#5A9692',
  CUSTOM: '#5DB8B3',
  UNKNOWN: '#CCCCCC',
  UNKNOWN_DEFAULT: '#E0E0E0'
};

export const GENDER_DISPLAY_NAME_MAP: Record<InstagramCreatorGender, string> = {
  MALE: 'Male',
  FEMALE: 'Female',
  CUSTOM: 'Custom',
  UNKNOWN: 'Unknown',
};

// Helper function to safely process percentage values
const safeProcessPercentage = (value: number | null | undefined): number => {
  if (value == null || isNaN(value) || value < 0) return 0;
  return parseFloat(value.toFixed(1));
};

// Helper function to validate and process distribution data
const processDistributionData = <T extends { key: string; value: number; name: string; fill: string }>(
  rawData: any[] | null | undefined,
  validator: (item: any) => boolean,
  mapper: (item: any) => T,
  colorMap: Record<string, string>,
  unknownKey: string,
  unknownName: string,
): T[] => {
  if (!rawData?.length) return [];

  try {
    const processedData = rawData
      .filter(validator)
      .map(mapper);

    // Calculate total percentage
    const totalPercentage = processedData.reduce((sum, item) => sum + item.value, 0);

    // Handle remainder if total is not 100%
    if (totalPercentage < 99.9 && totalPercentage >= 0) {
      const remainderPercentage = safeProcessPercentage(100.0 - totalPercentage);

      if (remainderPercentage > 0.0) {
        const unknownIndex = processedData.findIndex((item) => item.key === unknownKey);

        if (unknownIndex !== -1) {
          processedData[unknownIndex].value = safeProcessPercentage(processedData[unknownIndex].value + remainderPercentage);
        } else {
          processedData.push({
            key: unknownKey,
            name: unknownName,
            value: remainderPercentage,
            fill: colorMap[unknownKey] || colorMap.UNKNOWN_DEFAULT,
          } as T);
        }
      }
    }

    return processedData.sort((a, b) => b.value - a.value);
  } catch (error) {
    console.error('Error processing distribution data:', error);
    return [];
  }
};

export const useMajorAudienceDemographicsProps = (
  creator: InstagramCreator | null | undefined,
  isLoading: boolean,
  defaultTab: string = 'age',
) => {
  const [activeDemographicTab, setActiveDemographicTab] = useState<string>(defaultTab);

  const { ageBucket, gender, country, city } = useMemo(() => {
    try {
      const audience = creator?.insights?.audience;
      if (!audience) {
        return { ageBucket: '--', gender: '--', country: '--', city: '--' };
      }

      const { ageDistribution = [], genderDistribution = [], topCountries = [], topCities = [] } = audience;

      const topAgeBucket = ageDistribution?.reduce((max, current) => {
        return (current.percentage > (max?.percentage ?? 0)) ? current : max;
      });

      const rawAgeBucket = topAgeBucket?.ageBucket;
      const ageBucket = rawAgeBucket ? ageRangeDictionary[rawAgeBucket as InstagramCreatorAgeBucket] || '--' : '--';

      const topGender = genderDistribution?.reduce((max, current) => {
        return (current.percentage > (max?.percentage ?? 0)) ? current : max;
      });

      const rawGender = topGender?.gender;
      const gender = rawGender ? rawGender.toLowerCase() : '--';

      const topCountry = topCountries?.reduce((max, current) => {
        return (current.percentage > (max?.percentage ?? 0)) ? current : max;
      });
      const country = topCountry?.country ? getCountryLabel(topCountry?.country) : '--';

      const topCity = topCities?.reduce((max, current) => {
        return (current.percentage > (max?.percentage ?? 0)) ? current : max;
      });
      const city = topCity?.city || '--';

      return { ageBucket, gender, country, city };
    } catch (error) {
      console.error('Error processing basic demographics:', error);
      return { ageBucket: '--', gender: '--', country: '--', city: '--' };
    }
  }, [creator?.insights?.audience]);

  const ageDistributionData = useMemo(() => {
    const ageDistributionRaw = creator?.insights?.audience?.ageDistribution;

    return processDistributionData<AgeDistributionChartItem>(
      ageDistributionRaw,
      (item): item is InstagramCreatorAudienceAgeBucketEntry & { ageBucket: InstagramCreatorAgeBucket; percentage: number } =>
        item != null && item.ageBucket != null && item.percentage != null,
      (item) => ({
        key: item.ageBucket,
        name: ageRangeDictionary[item.ageBucket] || item.ageBucket,
        value: safeProcessPercentage(item.percentage),
        fill: AGE_BUCKET_COLOR_MAP[item.ageBucket as InstagramCreatorAgeBucket] || AGE_BUCKET_COLOR_MAP.UNKNOWN_DEFAULT,
      }),
      AGE_BUCKET_COLOR_MAP,
      'UNKNOWN',
      ageRangeDictionary.UNKNOWN || 'Unknown'
    );
  }, [creator?.insights?.audience?.ageDistribution]);

  const genderDistributionData = useMemo(() => {
    const genderDistributionRaw = creator?.insights?.audience?.genderDistribution;

    return processDistributionData<GenderDistributionChartItem>(
      genderDistributionRaw,
      (item): item is InstagramCreatorAudienceGenderEntry & { gender: InstagramCreatorGender; percentage: number } =>
        item != null && item.gender != null && item.percentage != null && item.percentage > 0,
      (item) => ({
        key: item.gender,
        name: GENDER_DISPLAY_NAME_MAP[item.gender] || GENDER_DISPLAY_NAME_MAP.UNKNOWN,
        value: safeProcessPercentage(item.percentage),
        fill: GENDER_COLOR_MAP[item.gender] || GENDER_COLOR_MAP.UNKNOWN_DEFAULT,
      }),
      GENDER_COLOR_MAP,
      'UNKNOWN',
      GENDER_DISPLAY_NAME_MAP.UNKNOWN
    );
  }, [creator?.insights?.audience?.genderDistribution]);

  const { topCountriesData, topCitiesData } = useMemo(() => {
    try {
      return {
        topCountriesData: creator?.insights?.audience?.topCountries || [],
        topCitiesData: creator?.insights?.audience?.topCities || [],
      };
    } catch (error) {
      console.error('Error processing location data:', error);
      return { topCountriesData: [], topCitiesData: [] };
    }
  }, [creator?.insights?.audience?.topCountries, creator?.insights?.audience?.topCities]);

  return {
    activeDemographicTab,
    setActiveDemographicTab,
    isLoading,
    ageBucket,
    gender,
    country,
    city,
    ageDistributionData,
    genderDistributionData,
    topCountriesData,
    topCitiesData,
  };
};
